import {Tailwind} from '@wandb/weave/components/Tailwind';
import * as React from 'react';
import {useContext, useMemo} from 'react';

import {maybePluralizeWord} from '../../../util/uihelpers';
import {Pagination} from '../../Pagination';
import {formatNumber} from '../../util/formatNumber';
import {InstrumentedCenteredWaveLoader as Loader} from '../../utility/InstrumentedLoader';
import {SingleRegistrySearchContext} from '../common/RegistriesSearchContextProvider';
import {SingleRegistrySearchBar} from '../common/SingleRegistrySearchBar';
import {
  CardCollectionType,
  RegistrySearchCollectionType,
} from '../common/types';
import {
  ArtifactCollectionCard,
  SingleRegistryCardCollection,
} from './ArtifactCollectionCard';
import {NoCollectionsFound} from './NoCollectionsFound';

type ArtifactCollectionCardsProps = {
  totalCount: number;
  currentPaginatedCollections: RegistrySearchCollectionType[];
  page: number;
  setPage: (val: number) => void;
  refetchCollections?: () => void;
  collectionsLoading: boolean;
  totalCollectionsLoaded: number;
  hasNextPage: boolean;
  pageSize: number;
  protectedAliases?: string[];
};

export const ArtifactCollectionCards = ({
  totalCount,
  currentPaginatedCollections,
  page,
  setPage,
  refetchCollections,
  collectionsLoading,
  totalCollectionsLoaded,
  hasNextPage,
  pageSize,
  protectedAliases,
}: ArtifactCollectionCardsProps) => {
  const {searchFilter} = useContext(SingleRegistrySearchContext);
  const collections: SingleRegistryCardCollection[] = useMemo(() => {
    return currentPaginatedCollections.map(collection => {
      return {
        collection,
        type: CardCollectionType.SINGLE_REGISTRY,
      };
    });
  }, [currentPaginatedCollections]);

  const displayCollectionCount =
    searchFilter != null
      ? `${formatNumber(totalCollectionsLoaded)}${
          hasNextPage ? '+' : ''
        } ${maybePluralizeWord(totalCollectionsLoaded, 'collection')}`
      : `${formatNumber(totalCount)} ${maybePluralizeWord(
          totalCount,
          'collection'
        )}`;

  return (
    <Tailwind style={{height: '100%', width: '100%'}}>
      <div className="pl-4 text-base font-semibold leading-8 text-moon-650">
        {displayCollectionCount}
      </div>
      <SingleRegistrySearchBar />
      {!collectionsLoading && totalCollectionsLoaded === 0 && (
        <NoCollectionsFound />
      )}
      <div className="relative">
        {/* Display a less-obtrusive loader to indicate that collections are still loading, even if some are displayed */}
        <div className="absolute inset-x-0 top-0 flex items-start justify-center">
          {collectionsLoading && (
            <Loader name="new-model-registry-search" size="huge" />
          )}
        </div>
        {/* Fade existing collections while loading or waiting on search results */}
        <div
          className={
            collectionsLoading && collections.length === 0
              ? 'pointer-events-none opacity-50'
              : ''
          }>
          <div className="mb-8 mt-14 flex justify-end">
            {collections.length > 0 && (
              <Pagination
                itemCount={totalCollectionsLoaded}
                page={page}
                perPage={pageSize}
                onPageChange={pageVal => setPage(pageVal)}
                allItemsLoaded={!hasNextPage}
              />
            )}
          </div>
          {collections.map(collectionInfo => {
            return (
              <ArtifactCollectionCard
                key={collectionInfo.collection.collectionId}
                registryName={
                  collectionInfo.collection.registryName ??
                  collectionInfo.collection.projectName
                }
                collectionInfo={collectionInfo}
                refetchCollections={refetchCollections}
                protectedAliases={protectedAliases}
              />
            );
          })}
        </div>
      </div>
    </Tailwind>
  );
};
